import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api'; // Import your API endpoints

const WhatWeDo = () => {
  const [thematicAreas, setThematicAreas] = useState([]);
  const [activeTab, setActiveTab] = useState('peace-governance');
  const { slug } = useParams();

  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchThematicAreas();
    fetchHeroImages();
    
    // Auto-slide hero images
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, [heroImages.length]);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.HERO_IMAGES, {
        params: { is_active: 1 }
      });
      if (response.data.success) {
        setHeroImages(response.data.data.map(img => img.image_path));
      } else {
        setHeroImages([
          '/images/hero1.jpg',
          '/images/hero2.jpg',
          '/images/hero3.jpg'
        ]);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      setHeroImages([
        '/images/hero1.jpg',
        '/images/hero2.jpg',
        '/images/hero3.jpg'
      ]);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const fetchThematicAreas = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.THEMATIC_AREAS);
      if (response.data.success) {
        setThematicAreas(response.data.data);
        if (response.data.data.length > 0 && !slug) {
          setActiveTab(response.data.data[0].slug || response.data.data[0].title.toLowerCase().replace(/\s+/g, '-'));
        }
      }
    } catch (error) {
      console.error('Error fetching thematic areas:', error);
      // Fallback data for development
      setThematicAreas([
        {
          id: 1,
          title: 'Peace Building & Governance',
          description: 'Promoting dialogue, reconciliation and good governance among communities',
          full_content: '<p>Our Peace Building & Governance program focuses on fostering peaceful coexistence and strengthening governance structures in conflict-prone areas. We work with community leaders, youth, women, and local authorities to build sustainable peace mechanisms.</p><p>Through dialogue forums, peace committees, and governance training, we empower communities to resolve conflicts peacefully and participate in decision-making processes.</p>',
          slug: 'peace-governance',
          icon: 'bi-handshake'
        },
        {
          id: 2,
          title: 'Humanitarian Assistance',
          description: 'Providing emergency relief and support to vulnerable communities affected by conflicts and disasters',
          full_content: '<p>Our Humanitarian Assistance program delivers life-saving aid to communities affected by conflicts, droughts, and other emergencies. We provide food assistance, water, sanitation, and essential non-food items.</p><p>We prioritize the most vulnerable groups including women, children, the elderly, and persons with disabilities, ensuring their protection and dignity is maintained during crises.</p>',
          slug: 'humanitarian',
          icon: 'bi-heart'
        },
        {
          id: 3,
          title: 'Sustainable Livelihoods',
          description: 'Enhancing economic opportunities, food security, and environmental sustainability',
          full_content: '<p>The Sustainable Livelihoods program promotes economic empowerment through skills training, micro-enterprise development, and sustainable agriculture practices.</p><p>We support communities in adopting climate-smart agriculture, establishing savings groups, and developing income-generating activities that promote long-term self-reliance.</p>',
          slug: 'livelihoods',
          icon: 'bi-tree'
        },
        {
          id: 4,
          title: 'Health & Nutrition',
          description: 'Improving healthcare access, nutrition outcomes, and health system strengthening',
          full_content: '<p>Our Health & Nutrition program focuses on improving maternal and child health, preventing malnutrition, and strengthening community health systems.</p><p>We conduct health education, support nutrition programs, provide basic health services, and work to improve access to clean water and sanitation facilities in underserved communities.</p>',
          slug: 'health',
          icon: 'bi-heart-pulse'
        },
        {
          id: 5,
          title: 'Education & Youth Empowerment',
          description: 'Supporting quality education, skills development, and youth leadership',
          full_content: '<p>The Education & Youth Empowerment program works to improve access to quality education, promote vocational training, and develop youth leadership skills.</p><p>We support school infrastructure, provide scholarships, conduct life skills training, and facilitate youth engagement in community development activities.</p>',
          slug: 'education',
          icon: 'bi-mortarboard'
        },
        {
          id: 6,
          title: 'Climate Change & Environment',
          description: 'Promoting environmental conservation, climate adaptation, and sustainable resource management',
          full_content: '<p>Our Climate Change & Environment program focuses on building community resilience to climate change through environmental conservation and sustainable natural resource management.</p><p>We promote tree planting, soil conservation, water harvesting, and climate-smart agricultural practices while advocating for environmentally sustainable policies.</p>',
          slug: 'climate',
          icon: 'bi-cloud-sun'
        }
      ]);
      
      // Set initial active tab if no slug in URL
      if (!slug) {
        setActiveTab('peace-governance');
      }
    }
  };

  const activeArea = thematicAreas.find(area => 
    area.slug === activeTab || 
    area.title.toLowerCase().replace(/\s+/g, '-') === activeTab
  );

  return (
    <div className="what-we-do-page">
      <style>{`
        .hero-section {
          min-height: 60vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slider {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: opacity 1.5s ease-in-out, transform 8s ease-out;
        }

        .hero-slide.active {
          animation: kenBurns 8s ease-out;
        }

        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot.active {
          background: white;
          transform: scale(1.2);
        }
      `}</style>
      
      {/* Hero */}
      <section className="hero-section position-relative text-white">
        {/* Background Image Slider */}
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentSlide ? 1 : 0,
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.1)',
              }}
            />
          ))}
          
          {/* Overlay */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.85) 0%, rgba(108, 117, 125, 0.75) 100%)'
            }}
          />
        </div>

        {/* Content */}
        <div className="container position-relative" style={{ zIndex: 2, paddingTop: '100px', paddingBottom: '100px' }}>
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-3 fw-bold text-white mb-4">What We Do</h1>
              <p className="lead text-white mb-0">
                Our programs are designed around key thematic areas that address 
                the root causes of conflict and promote sustainable development.
              </p>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="py-4 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="nav nav-pills justify-content-center flex-wrap" id="thematicTabs">
                {thematicAreas.map((area) => {
                  const areaSlug = area.slug || area.title.toLowerCase().replace(/\s+/g, '-');
                  return (
                    <button
                      key={area.id}
                      className={`nav-link ${activeTab === areaSlug ? 'active' : ''} mx-2 my-1`}
                      onClick={() => setActiveTab(areaSlug)}
                    >
                      {area.title}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Active Tab Content */}
      <section className="section-padding">
        <div className="container">
          {activeArea ? (
            <div className="row">
              <div className="col-lg-8">
                <h2 className="display-5 fw-bold mb-4">{activeArea.title}</h2>
                <div className="content-wrapper" dangerouslySetInnerHTML={{ 
                  __html: activeArea.full_content || activeArea.description || 
                  `<p>${activeArea.description || 'Detailed information about this thematic area will be available soon.'}</p>`
                }} />
                
                {/* Program Highlights */}
                {/*<div className="row mt-5">
                  <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <h5 className="card-title">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          Key Approaches
                        </h5>
                        <ul className="list-unstyled">
                          <li className="mb-2"><i className="bi bi-arrow-right me-2 text-primary"></i> Community Engagement</li>
                          <li className="mb-2"><i className="bi bi-arrow-right me-2 text-primary"></i> Capacity Building</li>
                          <li className="mb-2"><i className="bi bi-arrow-right me-2 text-primary"></i> Policy Advocacy</li>
                          <li><i className="bi bi-arrow-right me-2 text-primary"></i> Innovative Solutions</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <h5 className="card-title">
                          <i className="bi bi-graph-up text-warning me-2"></i>
                          Expected Outcomes
                        </h5>
                        <ul className="list-unstyled">
                          <li className="mb-2"><i className="bi bi-check me-2 text-success"></i> Reduced conflicts</li>
                          <li className="mb-2"><i className="bi bi-check me-2 text-success"></i> Improved livelihoods</li>
                          <li className="mb-2"><i className="bi bi-check me-2 text-success"></i> Enhanced resilience</li>
                          <li><i className="bi bi-check me-2 text-success"></i> Sustainable development</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div> */}
              </div>
              
              <div className="col-lg-4">
                <div className="sticky-top" style={{top: '100px'}}>
                  {/* Quick Links to Other Areas */}
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                      <h5 className="card-title mb-3">Other Thematic Areas</h5>
                      <div className="list-group list-group-flush">
                        {thematicAreas
                          .filter(area => {
                            const areaSlug = area.slug || area.title.toLowerCase().replace(/\s+/g, '-');
                            return areaSlug !== activeTab;
                          })
                          .map((area) => {
                            const areaSlug = area.slug || area.title.toLowerCase().replace(/\s+/g, '-');
                            return (
                              <button
                                key={area.id}
                                className="list-group-item list-group-item-action border-0"
                                onClick={() => setActiveTab(areaSlug)}
                              >
                                <i className="bi bi-arrow-right me-2"></i>
                                {area.title}
                              </button>
                            );
                          })
                        }
                      </div>
                    </div>
                  </div>
                  
                  {/* Related Projects */}
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h5 className="card-title mb-3">Related Projects</h5>
                      <p className="text-muted">
                        View projects related to {activeArea.title}
                      </p>
                      <Link 
                        to={`/projects?area=${activeArea.slug || activeArea.title.toLowerCase().replace(/\s+/g, '-')}`} 
                        className="btn btn-primary w-100"
                      >
                        View Projects
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-5 bg-light">
        <div className="container text-center">
          <h2 className="display-5 fw-bold mb-4">Want to Learn More?</h2>
          <p className="lead mb-5">
            Contact us for detailed information about our programs and how you can get involved.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Link to="/contact" className="btn btn-primary btn-lg px-5">
              Contact Us
            </Link>
            <Link to="/projects" className="btn btn-outline-primary btn-lg px-5">
              View Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatWeDo;